export function normalizeText(value) {
  return (value || "")
    .normalize("NFD")
    .replace(/[̀-ͯ]/g, "")
    .toLowerCase()
    .trim();
}

// Recebe a lista escopada de api/Team já com o caminho de unidades ancestrais
// (via $Utilities.teamsWithPath) e se o próprio nó é uma equipe (isTeam) —
// e monta a árvore Unidade > Subunidade > Equipe.
// Raízes sem unidade (equipes soltas) ficam no mesmo array de retorno.
export function buildTeamHierarchy(items) {
  const nodeMap = new Map();
  const roots = [];

  function ensureNode(id, name, isTeam) {
    const key = String(id);
    let node = nodeMap.get(key);
    if (!node) {
      node = { id, name, isTeam, children: [] };
      nodeMap.set(key, node);
    } else {
      node.name = name;
      node.isTeam = isTeam;
    }
    return node;
  }

  items.forEach((item) => {
    let parentNode = null;
    (item.path || []).forEach((ancestor) => {
      const node = ensureNode(ancestor.id, ancestor.name, false);
      attach(node, parentNode);
      parentNode = node;
    });
    const leaf = ensureNode(item.id, item.name, item.isTeam);
    attach(leaf, parentNode);
  });

  function attach(node, parentNode) {
    if (parentNode) {
      if (!parentNode.children.includes(node)) parentNode.children.push(node);
    } else if (!roots.includes(node)) {
      roots.push(node);
    }
  }

  sortChildren(roots);
  return roots;
}

function sortChildren(nodes) {
  nodes.forEach((node) => sortChildren(node.children));
  nodes.sort((a, b) => {
    if (a.isTeam !== b.isTeam) return a.isTeam ? -1 : 1;
    return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });
  });
}

// Ordena as raízes: unidades primeiro (alfabética), equipes sem unidade por
// último (alfabética), conforme o layout pede.
export function sortRoots(roots) {
  return [...roots].sort((a, b) => {
    if (a.isTeam !== b.isTeam) return a.isTeam ? 1 : -1;
    return a.name.localeCompare(b.name, "pt-BR", { sensitivity: "base" });
  });
}

export function countDirectChildren(node) {
  const equipes = node.children.filter((child) => child.isTeam).length;
  const subunidades = node.children.filter((child) => !child.isTeam).length;
  return { equipes, subunidades };
}

export function childrenSummary(node) {
  const { equipes, subunidades } = countDirectChildren(node);
  const parts = [];
  if (equipes) parts.push(`${equipes} ${equipes === 1 ? "equipe" : "equipes"}`);
  if (subunidades) {
    parts.push(`${subunidades} ${subunidades === 1 ? "subunidade" : "subunidades"}`);
  }
  return parts.length ? parts.join(" · ") : "Nenhuma equipe";
}

// Conta todos os descendentes (para o aviso de exclusão).
export function countDescendants(node) {
  let equipes = 0;
  let subunidades = 0;
  const stack = [...node.children];
  while (stack.length) {
    const current = stack.pop();
    if (current.isTeam) equipes += 1;
    else subunidades += 1;
    stack.push(...current.children);
  }
  return { equipes, subunidades };
}

// Filtra a árvore por termo de busca: mantém ancestrais de um item que bate
// e, quando a própria unidade bate, mantém toda a subárvore dela.
export function filterHierarchy(nodes, rawTerm) {
  const term = normalizeText(rawTerm);
  if (!term) return nodes;

  const result = [];
  nodes.forEach((node) => {
    const selfMatch = normalizeText(node.name).includes(term);
    if (selfMatch) {
      result.push(node);
      return;
    }
    const filteredChildren = filterHierarchy(node.children, rawTerm);
    if (filteredChildren.length) {
      result.push({ ...node, children: filteredChildren });
    }
  });
  return result;
}
